import axios from 'axios'
import { api } from '@services/api'
import { logger } from '@utils/logger'

// AI Service base URL
// Use proxy in development, direct URL in production
const AI_BASE_URL = 
  import.meta.env.MODE === 'development'
    ? '/ai-api' // Use Vite proxy in development
    : import.meta.env.VITE_AI_API_BASE_URL || 'http://localhost:8000/api/v1'

export const aiApi = axios.create({
  baseURL: AI_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds for AI requests
})

// Request interceptor for AI API
aiApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
aiApi.interceptors.response.use(
  async (response) => {
    await logger.info('AI API Response Success', {
      url: response.config.url,
      method: response.config.method?.toUpperCase(),
      status: response.status,
    })
    return response
  },
  async (error) => {
    await logger.error('AI API Response Error', error, {
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
    })
    return Promise.reject(error)
  }
)

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp?: string
  messageId?: number // ID from backend for feedback
  feedbackUsed?: boolean | null // If mechanic used the instructions
  feedbackWorked?: boolean | null // If it worked after using
  // Internal tracking properties
  _alreadyDisplayed?: boolean
  _isHistorical?: boolean
  position?: number
}

export interface AIRecommendationRequest {
  user_id?: number
  company_id?: number
  vehicle_id?: number
  piece_type?: string
  query?: string
  limit?: number
}

export interface AIPricingRequest {
  piece_id?: number
  company_id?: number
  quantity?: number
  base_price?: number
  query?: string
}

export interface AIDemandForecastRequest {
  piece_id?: number
  company_id?: number
  forecast_days?: number
  query?: string
}

export interface ExtractedWorkOrderOption {
  option_number?: number
  diagnosis?: string
  description?: string
  estimated_hours?: number
  estimated_cost?: number
  success_probability?: number
  parts_needed?: string
  services_needed?: string
  parts?: Array<Record<string, unknown>>
  services?: Array<Record<string, unknown>>
}

export interface ExtractWorkOrderDataResult {
  options?: ExtractedWorkOrderOption[]
  recommended_option?: number
  diagnosis?: string
  parts?: Array<{
    description: string
    quantity: number
    estimated_price?: number
  }>
  services?: Array<{
    description: string
    hours: number
    hourly_rate?: number
  }>
  observations?: string
}

export interface ChatRequest {
  message: string
  conversation_history?: ChatMessage[]
  company_id?: number
  user_id?: number
  context?: Record<string, any>
}

export interface ChatResponse {
  message: string
  type: string
  data?: any
  timestamp?: string
}

export const aiService = {
  // Chat endpoint - process natural language queries using the AI chat API
  chat: async (message: string, context?: { companyId?: number; userId?: number }, conversationHistory?: ChatMessage[]): Promise<ChatResponse> => {
    try {
      const response = await aiApi.post<ChatResponse>('/chat', {
        message: message.trim(),
        conversation_history: conversationHistory || [],
        company_id: context?.companyId,
        user_id: context?.userId,
        context: context,
      })
      
      return response.data
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Erro ao processar mensagem'
      throw new Error(errorMessage)
    }
  },

  // Get recommendations
  getRecommendations: async (request: AIRecommendationRequest) => {
    const response = await aiApi.post('/recommendations/parts', {
      user_id: request.user_id,
      company_id: request.company_id,
      vehicle_id: request.vehicle_id,
      piece_type: request.piece_type,
      limit: request.limit || 10,
    })
    return {
      type: 'recommendations',
      message: 'Aqui estão algumas recomendações de peças:',
      data: response.data,
    }
  },

  // Get pricing suggestions
  getPricingSuggestion: async (request: AIPricingRequest) => {
    if (request.piece_id) {
      const response = await aiApi.get(`/pricing/market-analysis/${request.piece_id}`)
      return {
        type: 'pricing',
        message: 'Análise de preços:',
        data: response.data,
      }
    }
    return {
      type: 'pricing',
      message: 'Para análise de preços, preciso do ID da peça. Por favor, especifique qual peça deseja analisar.',
      data: null,
    }
  },

  // Get demand forecast
  getDemandForecast: async (request: AIDemandForecastRequest) => {
    if (request.piece_id) {
      const response = await aiApi.post('/forecasting/demand', {
        piece_id: request.piece_id,
        company_id: request.company_id,
        forecast_days: request.forecast_days || 30,
        include_seasonality: true,
      })
      return {
        type: 'forecast',
        message: 'Previsão de demanda:',
        data: response.data,
      }
    }
    if (request.company_id) {
      const response = await aiApi.get(`/forecasting/inventory/${request.company_id}?days=${request.forecast_days || 30}`)
      return {
        type: 'forecast',
        message: 'Previsão de estoque:',
        data: response.data,
      }
    }
    return {
      type: 'forecast',
      message: 'Para previsão de demanda, preciso do ID da peça ou da empresa. Por favor, especifique.',
      data: null,
    }
  },

  // Health check
  healthCheck: async () => {
    const response = await aiApi.get('/health')
    return response.data
  },

  // Extract work order data from conversation
  extractWorkOrderData: async (conversationHistory: ChatMessage[]): Promise<ExtractWorkOrderDataResult> => {
    try {
      const response = await api.post<{ data?: ExtractWorkOrderDataResult } | ExtractWorkOrderDataResult>(
        '/ai/extract-work-order',
        { conversation_history: conversationHistory }
      )
      const payload = response.data
      if (payload && typeof payload === 'object' && 'data' in payload && payload.data) {
        return payload.data
      }
      return (payload ?? {}) as ExtractWorkOrderDataResult
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Erro ao extrair dados da conversa'
      throw new Error(errorMessage)
    }
  },
}

export default aiService

